import * as React from 'react'

import invariant from 'invariant'
import compact from 'lodash/compact'
import flatten from 'lodash/flatten'
import flattenDeep from 'lodash/flattenDeep'
import groupBy from 'lodash/groupBy'
import intersection from 'lodash/intersection'
import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import {useLocation, useNavigate} from 'react-router'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'
import {
  openAddDynamicProductionTimeModal,
  openAddProviderInfoDimensionsModal,
  openAddProviderInfoPricingModal,
  openAddProviderInfoPrintingMethodModal,
  openAddProviderInfoProductionTimesModal,
  openConfirmDeleteModal,
  openConfirmToggleAvailabilityModal,
  openMaterialModal,
} from '../../redux/features/modal'

import useAsync from '../../../../commons/src/hooks/use-async'
import useSearch from '../../../../commons/src/hooks/use-search'
import {useExpandedRows} from '../../hooks/use-expanded-rows'
import useOnBeforeUnload from '../../hooks/use-on-before-unload'
import useSelectedIds from '../../hooks/use-selected-ids'
import useTableRowFilters from '../../hooks/use-table-row-filters'
import useWindowScrollYPosition from '../../hooks/use-window-scroll-position'
import getCloudinaryUrl from '../../libs/cloudinary'
import {
  formatDimension,
  formatInfillQuoteType,
  formatInfillQuoteTypesSorted,
  formatTimeRange,
} from '../../libs/formatter'
import {getMissingProviderInfoData} from '../../libs/provider-info'
import {
  BooleanMatchingFilter,
  StringMatchingFilter,
  filterTableRows,
} from '../../libs/table-row-filter'
import * as dynamicProductionTimeApi from '../../services/api/dynamic-production-time'
import * as optionApi from '../../services/api/option'
import * as priceApi from '../../services/api/price'
import * as providerInfoApi from '../../services/api/provider-info'
import {
  goToCreateProviderInfo,
  goToEditProviderInfo,
  goToProviderInfos,
} from '../../services/navigation'
import {createProviderInfoSearch} from '../../services/search'

import {MaterialTechnology} from '../../../../commons/src/types'
import {
  EditProviderInfoTab,
  FinishGroupId,
  MaterialId,
  Option,
  ProviderInfoFilterLocationState,
  ProviderInfoId,
  ProviderInfoRowContent,
} from '../../types'

import deleteIcon from '../../../../commons/src/assets/icons/delete.svg'
import editIcon from '../../../../commons/src/assets/icons/edit.svg'
import infillIcon from '../../../../commons/src/assets/icons/infill.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import CheckboxField from '../../../../commons/src/components/checkbox-field'
import ContextMenu from '../../../../commons/src/components/context-menu'
import ContextMenuLink from '../../../../commons/src/components/context-menu/context-menu-link'
import CounterTag from '../../../../commons/src/components/counter-tag'
import FilterBar from '../../../../commons/src/components/filter-bar'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Link from '../../../../commons/src/components/link'
import LinkButton from '../../../../commons/src/components/link-button'
import MaterialImage from '../../../../commons/src/components/material-image'
import Paragraph from '../../../../commons/src/components/paragraph/paragraph'
import PopoverHandler from '../../../../commons/src/components/popover-handler'
import SearchInputField from '../../../../commons/src/components/search-input-field'
import Stack from '../../../../commons/src/components/stack'
import ToggleField from '../../../../commons/src/components/toggle-field'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import ActionHeaderLayout from '../../components/action-header-layout'
import ActionHeader from '../../components/action-header-layout/action-header'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'
import StatusDot from '../../components/status-dot'
import Table from '../../components/table'
import ExpandableTableRow from '../../components/table/ExpandableTableRow'
import TableCell from '../../components/table/TableCell'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableRow from '../../components/table/TableRow'
import TableRowToggleButton from '../../components/table/TableRowToggleButton'

import BooleanMatchingFilterPartial from '../filters/boolean-matching-filter-partial'
import StringMatchingFilterPartial from '../filters/string-matching-filter-partial'
import PagePartial from '../page-partial'

const getNumberOfUniquePrintingMethods = (providerInfoRows: ProviderInfoRowContent[]) => {
  const items = uniq(providerInfoRows.map(row => row.printingMethodId))

  return items.length
}

const getNumberOfValidPrintingMethods = (providerInfoRows: ProviderInfoRowContent[]) => {
  const items = uniq(
    providerInfoRows.map(row => row.printingMethodId).filter(printingMethodId => !!printingMethodId)
  )
  return items.length
}

const getNumberOfUniqueDimensions = (providerInfoRows: ProviderInfoRowContent[]) => {
  const items = uniq(
    providerInfoRows.map(row =>
      [
        row.minimumDimensions ? formatDimension(row.minimumDimensions) : '',
        row.maximumDimensions ? formatDimension(row.maximumDimensions) : '',
      ].join('-')
    )
  )

  return items.length
}

const getNumberOfUniqueProductionTimes = (providerInfoRows: ProviderInfoRowContent[]) => {
  const items = uniq(
    providerInfoRows.map(row => [row.productionTimeFast, row.productionTimeSlow].join('-'))
  )

  return items.length
}

const getNumberOfUniquePricings = (providerInfoRows: ProviderInfoRowContent[]) => {
  const items = uniq(providerInfoRows.map(row => row.priceId))

  return items.length
}

const getNumberOfUniqueDynamicProductionTime = (providerInfoRows: ProviderInfoRowContent[]) => {
  const items = uniq(providerInfoRows.map(row => row.dynamicProductionTimeId))

  return items.length
}

const createInitialFiltersMap = {
  technology: (filterValues: string[] = []) =>
    new StringMatchingFilter<ProviderInfoRowContent>({
      key: 'technology',
      filterValues,
    }),
  printingMethod: (filterValues: string[] = []) =>
    new StringMatchingFilter<ProviderInfoRowContent>({
      key: 'printingMethodId',
      filterValues,
    }),
  price: (filterValues: string[] = []) =>
    new StringMatchingFilter<ProviderInfoRowContent>({
      key: 'priceId',
      filterValues,
    }),
  infill: (filterValues: string[] = []) =>
    new StringMatchingFilter<ProviderInfoRowContent>({
      key: 'infillOptions',
      filterValues,
    }),
  productionTime: (filterValues: string[] = []) =>
    new StringMatchingFilter<ProviderInfoRowContent>({
      key: 'productionTimeOptions',
      filterValues,
    }),
  dynamicProductionTime: (filterValues: string[] = []) =>
    new StringMatchingFilter<ProviderInfoRowContent>({
      key: 'dynamicProductionTimeId',
      filterValues,
    }),
  status: (filterValue?: boolean) =>
    new BooleanMatchingFilter<ProviderInfoRowContent>({
      key: 'available',
      filterValue,
    }),
}

// Use filter values from location state if provided
const getInitialFilters = (locationStateFilters: ProviderInfoFilterLocationState = {}) =>
  mapValues(createInitialFiltersMap, (createFilter, key) => {
    const locationStateFilterValues = locationStateFilters[key]
    return createFilter(
      locationStateFilterValues ? (locationStateFilterValues as string[] & boolean) : undefined
    )
  })

const statusFilterOptions = {
  undefined: 'Show all',
  true: 'Show only live materials',
  false: 'Hide all live materials',
}

const ProviderInfoPage = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  const materialConfigs = useAppSelector(state => state.core.materialConfigs)
  const colors = useAppSelector(state => state.core.colors)
  const materials = useAppSelector(state => state.core.materials)
  const finishGroups = useAppSelector(state => state.core.finishGroups)
  const printingMethods = useAppSelector(state => state.core.printingMethods)
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const resetLocationState = () => {
    goToProviderInfos(navigate, {
      initialFilters: undefined,
      replace: true,
    })
  }

  useOnBeforeUnload(resetLocationState)

  const {
    isLoading: isLoadingProviderInfos,
    data: providerInfos = [],
    refresh,
  } = useAsync(() => providerInfoApi.getWithOptionsByProvider(provider.id), [], {
    onError: error => dispatch(fatalError({error})),
    defaultLoading: true,
  })

  const {
    isLoading: isLoadingPrices,
    data: prices = [],
    refresh: refreshPrices,
  } = useAsync(() => priceApi.getByProvider(provider.id), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const {
    isLoading: isLoadingDynamicProductionTimes,
    data: dynamicProductionTimes = [],
    refresh: refreshDynamicProductionTimes,
  } = useAsync(() => dynamicProductionTimeApi.getByProvider(provider.id), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const {isLoading: isLoadingInfills, data: infills = []} = useAsync(
    () => optionApi.getInfillsByProvider(provider.id),
    [],
    {
      onError: error => dispatch(fatalError({error})),
      defaultLoading: true,
    }
  )

  const {isLoading: isLoadingProductionTimeOptions, data: productionTimes = []} = useAsync(
    () => optionApi.getProductionTimeOptionsByProvider(provider.id),
    [],
    {
      onError: error => dispatch(fatalError({error})),
      defaultLoading: true,
    }
  )

  const {filters, setFilter, resetFilters} = useTableRowFilters<ProviderInfoRowContent>(
    getInitialFilters(location.state?.initialFilters)
  )

  const isLoading =
    isLoadingProviderInfos ||
    isLoadingPrices ||
    isLoadingInfills ||
    isLoadingProductionTimeOptions ||
    isLoadingDynamicProductionTimes

  const pricesById = React.useMemo(() => keyBy(prices, price => price.id), [prices])

  const dynamicProductionTimesById = React.useMemo(
    () => keyBy(dynamicProductionTimes, time => time.id),
    [prices]
  )

  const infillsById = React.useMemo(() => keyBy(infills, infill => infill.id), [infills])
  const productionTimeOptionsById = React.useMemo(
    () => keyBy(productionTimes, productionTime => productionTime.id),
    [productionTimes]
  )

  const [tableRows, providerInfosById] = React.useMemo(() => {
    // Order provider infos by material name, finish group name, material config name
    const sortedProviderInfos = sortBy(
      providerInfos,
      providerInfo =>
        materials[
          finishGroups[materialConfigs[providerInfo.materialConfigId].finishGroupId].materialId
        ].name,
      providerInfo =>
        finishGroups[materialConfigs[providerInfo.materialConfigId].finishGroupId].name,
      providerInfo => materialConfigs[providerInfo.materialConfigId].name
    )

    const providerInfoTableRows: ProviderInfoRowContent[] = sortedProviderInfos.map(
      providerInfo => {
        const materialConfig = materialConfigs[providerInfo.materialConfigId]
        const finishGroup = finishGroups[materialConfig.finishGroupId]
        const material = materials[finishGroup.materialId]
        const providerInfoInfillOptions = compact(
          providerInfo.options.map(item => infillsById[item.id])
        )
        const providerInfoProductionTimeOptions = compact(
          providerInfo.options.map(item => productionTimeOptionsById[item.id])
        )

        return {
          ...providerInfo,
          name: providerInfo.materialName || material.name,
          technology: material.technology,
          printingMethodName: providerInfo.printingMethodId
            ? printingMethods[providerInfo.printingMethodId].name
            : null,
          infillTypeOptions: providerInfoInfillOptions.map(item => item.type),
          infillOptions: providerInfoInfillOptions.map(item => item.id),
          productionTimeTypeOptions: providerInfoProductionTimeOptions
            .map(item => item.type)
            .sort(),
          productionTimeOptions: providerInfoProductionTimeOptions.map(item => item.id),
        }
      }
    )

    return [providerInfoTableRows, keyBy(providerInfos, providerInfo => providerInfo.id)]
  }, [providerInfos])

  useWindowScrollYPosition('providerInfo_scrollY', !isLoading && tableRows.length > 0)

  const {searchResults, setSearchValue, searchValue} = useSearch(
    tableRows,
    createProviderInfoSearch
  )

  const filteredTableRows = React.useMemo(
    () => filterTableRows(tableRows, Object.values(filters)),
    [filters, tableRows]
  )

  const [materialTableRows, tableRowsByFinishGroup] = React.useMemo(() => {
    const searchedAndFilteredTableRows = intersection(searchResults, filteredTableRows)
    return [
      Object.values(
        groupBy(
          searchedAndFilteredTableRows,
          providerInfo =>
            finishGroups[materialConfigs[providerInfo.materialConfigId].finishGroupId].materialId
        )
      ),
      groupBy(
        searchedAndFilteredTableRows,
        providerInfo => materialConfigs[providerInfo.materialConfigId].finishGroupId
      ),
    ]
  }, [searchResults, filteredTableRows])

  const {toggleId, toggleAll, removeIds, isSelected, isAnySelected, areAllSelected, selectedIds} =
    useSelectedIds<ProviderInfoId>(() =>
      flatten(materialTableRows.map(rows => rows.map(row => row.id)))
    )

  const {isExpanded, isAnyExpanded, toggleExpansion, setAllExpanded} = useExpandedRows<
    MaterialId | FinishGroupId
  >(config.localStorageKeys.providerInfoExpandedIds, () =>
    flattenDeep(
      materialTableRows.map(materialRowItems =>
        materialRowItems
          .map(m => m.materialConfigId)
          .map(configId => {
            const finishGroup = finishGroups[materialConfigs[configId].finishGroupId]
            const material = materials[finishGroup.materialId]
            return [material.id, finishGroup.id]
          })
      )
    )
  )

  const materialTechnologyOptions = React.useMemo(
    () =>
      Object.values(MaterialTechnology).map(technology => {
        const label = config.technologyFilterMap[technology].label || technology
        return {
          id: technology,
          label,
          searchable: label,
        }
      }),
    []
  )

  const printingMethodFilterOptions = React.useMemo(() => {
    const sortedPrintingMethods = sortBy(printingMethods, [method => method.name])
    return Object.values(sortedPrintingMethods).map(printingMethod => ({
      id: printingMethod.id,
      label: printingMethod.name,
      searchable: printingMethod.name,
    }))
  }, [printingMethods])

  const priceFilterOptions = React.useMemo(() => {
    const sortedPrices = sortBy(Object.values(pricesById), price => price.name)
    return sortedPrices.map(price => ({
      id: price.id,
      label: price.name,
      searchable: price.name,
    }))
  }, [pricesById])

  const dynamicProductionTimeOptions = React.useMemo(() => {
    const sortedDynamicProductionTimes = sortBy(
      Object.values(dynamicProductionTimesById),
      item => item.name
    )
    return sortedDynamicProductionTimes.map(item => ({
      id: item.id,
      label: item.name,
      searchable: item.name,
    }))
  }, [dynamicProductionTimesById])

  const renderInfillLabel = (infill: Option, key: string, isTooltipContent = false) => (
    <ActionBar space="80" noWrap key={key}>
      <div>{infill.name || '-'}</div>
      <Paragraph minor size={isTooltipContent ? 'xs' : 's'} className="u-no-margin-bottom">
        <Icon source={infillIcon} /> {formatInfillQuoteType([infill.type])}
      </Paragraph>
    </ActionBar>
  )

  const renderProductionTimeOptionLabel = (
    productionTimeOption: Option,
    key: string,
    isTooltipContent = false
  ) => (
    <ActionBar space="80" noWrap key={key}>
      <div>{productionTimeOption.name || '-'}</div>
      <Paragraph minor size={isTooltipContent ? 'xs' : 's'} className="u-no-margin-bottom">
        {productionTimeOption.type}
      </Paragraph>
    </ActionBar>
  )

  const sortOptions = (options: Option[]) =>
    sortBy(options, [infill => Number(infill.type.replace('infill_', '')), infill => infill.name])

  const infillFilterOptions = React.useMemo(() => {
    const sortedInfills = sortOptions(Object.values(infillsById))
    return sortedInfills.map(infill => ({
      id: infill.id,
      label: renderInfillLabel(infill, `filterLabel_${infill.id}`),
      searchable: infill.name || '-',
    }))
  }, [infillsById])

  const productionTimeFilterOptions = React.useMemo(() => {
    const sortedOptions = sortBy(productionTimeOptionsById, [
      option => option.type,
      option => option.name,
    ])
    return sortedOptions.map(option => ({
      id: option.id,
      label: renderProductionTimeOptionLabel(option, `filterLabel_${option.id}`),
      searchable: option.name || '-',
    }))
  }, [productionTimeOptionsById])

  const refreshAndDeselect = () => {
    refresh()
    removeIds(selectedIds)
  }

  const handleMenuItemClick = (activeTab: EditProviderInfoTab) =>
    goToEditProviderInfo(navigate, {
      providerInfos: providerInfos.filter(providerInfo => selectedIds.includes(providerInfo.id)),
      activeTab,
    })

  const handleAvailableToggleUpdate = async (
    checked: boolean,
    providerInfoIds: ProviderInfoId[]
  ) => {
    try {
      await providerInfoApi.patchMultiple(
        {
          available: checked,
        },
        providerInfoIds
      )
    } catch (error) {
      dispatch(fatalError({error: error as Error}))
    }

    refresh()
  }

  const handleAvailableToggleChange = (checked: boolean, providerInfoIds: ProviderInfoId[]) => {
    if (providerInfoIds.length > 1) {
      dispatch(
        openConfirmToggleAvailabilityModal({
          enable: checked,
          onConfirm: () => handleAvailableToggleUpdate(checked, providerInfoIds),
        })
      )
    } else {
      handleAvailableToggleUpdate(checked, providerInfoIds)
    }
  }

  const renderPrintingMethodAndInfills = (
    currentProviderInfoRows: ProviderInfoRowContent[],
    missingData: Record<string, true>
  ) => {
    const numUniquePrintingMethods = getNumberOfUniquePrintingMethods(currentProviderInfoRows)
    const numValidPrintingMethods = getNumberOfValidPrintingMethods(currentProviderInfoRows)
    const rowInfillOptions = currentProviderInfoRows[0].infillOptions.map(
      infillId => infillsById[infillId]
    )

    const sortedRowInfillOptions = sortOptions(rowInfillOptions)

    if (numUniquePrintingMethods === 1 && numValidPrintingMethods === 1) {
      return (
        <span>
          {currentProviderInfoRows[0].printingMethodName}
          <br />
          {sortedRowInfillOptions.length > 0 && (
            <TooltipHandler
              openOnClick
              tooltip={
                <Tooltip>
                  {sortedRowInfillOptions.map((infill, index) =>
                    renderInfillLabel(infill, `${index}`, true)
                  )}
                </Tooltip>
              }
            >
              <Link appearance="noUnderline">
                {formatInfillQuoteTypesSorted(currentProviderInfoRows[0].infillTypeOptions)}
              </Link>
            </TooltipHandler>
          )}
        </span>
      )
    }
    if (numUniquePrintingMethods > 1 && numValidPrintingMethods > 0) {
      return (
        <span>
          Multi <CounterTag>{numUniquePrintingMethods}</CounterTag>{' '}
          {missingData.printingMethodId && <StatusDot status="warning" />}
        </span>
      )
    }

    const numAvailable = currentProviderInfoRows.filter(
      providerInfo => providerInfo.available
    ).length

    return (
      <LinkButton
        onClick={() =>
          dispatch(
            openAddProviderInfoPrintingMethodModal({
              providerInfos: currentProviderInfoRows,
              onDone: refresh,
            })
          )
        }
      >
        Add {numAvailable > 0 && <StatusDot status="warning" />}
      </LinkButton>
    )
  }

  const renderDimensions = (
    currentProviderInfoRows: ProviderInfoRowContent[],
    missingData: Record<string, true>
  ) => {
    const uniqueDimensions = getNumberOfUniqueDimensions(currentProviderInfoRows)

    if (uniqueDimensions === 1 && !currentProviderInfoRows[0].maximumDimensions) {
      const numAvailable = currentProviderInfoRows.filter(
        providerInfo => providerInfo.available
      ).length

      return (
        <LinkButton
          onClick={() =>
            dispatch(
              openAddProviderInfoDimensionsModal({
                providerInfos: currentProviderInfoRows,
                onDone: refreshAndDeselect,
              })
            )
          }
        >
          Add {numAvailable > 0 && <StatusDot status="warning" />}
        </LinkButton>
      )
    }

    if (uniqueDimensions === 1) {
      return (
        <>
          {currentProviderInfoRows[0].minimumDimensions && (
            <>
              min {formatDimension(currentProviderInfoRows[0].minimumDimensions)}
              <br />
            </>
          )}
          {currentProviderInfoRows[0].maximumDimensions && (
            <>max {formatDimension(currentProviderInfoRows[0].maximumDimensions)}</>
          )}
        </>
      )
    }

    return (
      <>
        Multi <CounterTag>{uniqueDimensions}</CounterTag>{' '}
        {missingData.maximumDimensions && <StatusDot status="warning" />}
      </>
    )
  }

  const renderProductionTimeAndOptions = (
    currentProviderInfoRows: ProviderInfoRowContent[],
    missingData: Record<string, boolean>
  ) => {
    const uniqueProductionTimes = getNumberOfUniqueProductionTimes(currentProviderInfoRows)

    const rowProductionTimeOptions = currentProviderInfoRows[0].productionTimeOptions.map(
      optionId => productionTimeOptionsById[optionId]
    )

    const sortedRowProductionTimeOptions = sortOptions(rowProductionTimeOptions)

    if (
      uniqueProductionTimes === 1 &&
      !currentProviderInfoRows[0].productionTimeFast &&
      !currentProviderInfoRows[0].productionTimeSlow
    ) {
      const numAvailable = currentProviderInfoRows.filter(
        providerInfo => providerInfo.available
      ).length

      return (
        <LinkButton
          onClick={() =>
            dispatch(
              openAddProviderInfoProductionTimesModal({
                providerId: provider.id,
                providerInfos: currentProviderInfoRows,
                onDone: refreshAndDeselect,
              })
            )
          }
        >
          Add {numAvailable > 0 && <StatusDot status="warning" />}
        </LinkButton>
      )
    }

    if (uniqueProductionTimes === 1) {
      const formattedProductionTime = formatTimeRange(
        currentProviderInfoRows[0].productionTimeFast || undefined,
        currentProviderInfoRows[0].productionTimeSlow || undefined
      )
      return (
        <span>
          {formattedProductionTime !== '' ? formattedProductionTime : undefined}
          <br />
          {sortedRowProductionTimeOptions.length > 0 && (
            <TooltipHandler
              openOnClick
              tooltip={
                <Tooltip>
                  {sortedRowProductionTimeOptions.map((option, index) =>
                    renderProductionTimeOptionLabel(option, `${index}`, true)
                  )}
                </Tooltip>
              }
            >
              <Link appearance="noUnderline">
                {currentProviderInfoRows[0].productionTimeTypeOptions.join(', ')}
              </Link>
            </TooltipHandler>
          )}
        </span>
      )
    }

    return (
      <>
        Multi <CounterTag>{uniqueProductionTimes}</CounterTag>{' '}
        {(missingData.productionTimeFast || missingData.pproductionTimeSlow) && (
          <StatusDot status="warning" />
        )}
      </>
    )
  }

  const renderDynamicProductionTimes = (
    currentProviderInfoRows: ProviderInfoRowContent[],
    missingData: Record<string, true>
  ) => {
    const uniqueDynamicProductionTimeIds =
      getNumberOfUniqueDynamicProductionTime(currentProviderInfoRows)

    if (
      uniqueDynamicProductionTimeIds === 1 &&
      !currentProviderInfoRows[0].dynamicProductionTimeId
    ) {
      return (
        <LinkButton
          onClick={() => {
            dispatch(
              openAddDynamicProductionTimeModal({
                providerId: provider.id,
                providerInfos: currentProviderInfoRows,
                onDone: async () => {
                  refresh()
                  refreshDynamicProductionTimes()
                },
              })
            )
          }}
        >
          Add
        </LinkButton>
      )
    }

    if (uniqueDynamicProductionTimeIds === 1) {
      return dynamicProductionTimesById[currentProviderInfoRows[0].dynamicProductionTimeId || 0]
        ?.name
    }

    return (
      <>
        Multi <CounterTag>{uniqueDynamicProductionTimeIds}</CounterTag>{' '}
      </>
    )
  }

  const renderPricing = (
    currentProviderInfoRows: ProviderInfoRowContent[],
    missingData: Record<string, true>
  ) => {
    const uniquePricings = getNumberOfUniquePricings(currentProviderInfoRows)

    if (uniquePricings === 1 && !currentProviderInfoRows[0].priceId) {
      const numAvailable = currentProviderInfoRows.filter(
        providerInfo => providerInfo.available
      ).length

      return (
        <LinkButton
          onClick={() =>
            dispatch(
              openAddProviderInfoPricingModal({
                providerId: provider.id,
                providerInfos: currentProviderInfoRows,
                onDone: async () => {
                  refresh()
                  refreshPrices()
                },
              })
            )
          }
        >
          Add {numAvailable > 0 && <StatusDot status="warning" />}
        </LinkButton>
      )
    }

    if (uniquePricings === 1) {
      return pricesById[currentProviderInfoRows[0].priceId || 0]?.name
    }

    return (
      <>
        Multi <CounterTag>{uniquePricings}</CounterTag>{' '}
        {missingData.priceId && <StatusDot status="warning" />}
      </>
    )
  }

  const renderMaterialConfigRow = (providerInfoRow: ProviderInfoRowContent) => {
    const materialConfig = materialConfigs[providerInfoRow.materialConfigId]
    const finishGroup = finishGroups[materialConfig.finishGroupId]
    const color = colors[materialConfig.colorId]

    const missingData = getMissingProviderInfoData(providerInfoRow)

    return (
      <TableRow key={materialConfig.id}>
        <>
          <TableCell>
            <CheckboxField
              checked={isSelected(providerInfoRow.id)}
              onChange={() => toggleId(providerInfoRow.id)}
            />
          </TableCell>
          <TableCell minor>{providerInfoRow.name}</TableCell>
          <TableCell minor>{finishGroup.name}</TableCell>
          <TableCell strong>
            <MaterialImage
              className="u-margin-right-xs"
              size="xs"
              square
              border
              image={color.image && getCloudinaryUrl(color.image, ['w_700', 'h_458', 'c_fill'])}
              color={color.hexCode}
            />
            {color.name}
          </TableCell>
          <TableCell>
            {renderPrintingMethodAndInfills([providerInfoRow], missingData) || (
              <LinkButton
                onClick={() =>
                  dispatch(
                    openAddProviderInfoPrintingMethodModal({
                      providerInfos: [providerInfoRow],
                      onDone: refreshAndDeselect,
                    })
                  )
                }
              >
                Add
              </LinkButton>
            )}
          </TableCell>
          <TableCell>
            {providerInfoRow.minimumDimensions || providerInfoRow.maximumDimensions ? (
              renderDimensions([providerInfoRow], missingData)
            ) : (
              <LinkButton
                onClick={() =>
                  dispatch(
                    openAddProviderInfoDimensionsModal({
                      providerInfos: [providerInfoRow],
                      onDone: refreshAndDeselect,
                    })
                  )
                }
              >
                Add
              </LinkButton>
            )}
          </TableCell>
          <TableCell>
            {renderProductionTimeAndOptions([providerInfoRow], missingData) || (
              <LinkButton
                onClick={() =>
                  dispatch(
                    openAddProviderInfoProductionTimesModal({
                      providerId: provider.id,
                      providerInfos: [providerInfoRow],
                      onDone: refreshAndDeselect,
                    })
                  )
                }
              >
                Add
              </LinkButton>
            )}
          </TableCell>
          <TableCell>
            {renderDynamicProductionTimes([providerInfoRow], missingData) || (
              <LinkButton
                onClick={() => {
                  dispatch(
                    openAddDynamicProductionTimeModal({
                      providerId: provider.id,
                      providerInfos: [providerInfoRow],
                      onDone: refreshAndDeselect,
                    })
                  )
                }}
              >
                Add
              </LinkButton>
            )}
          </TableCell>
          <TableCell>
            {renderPricing([providerInfoRow], missingData) || (
              <LinkButton
                onClick={() =>
                  dispatch(
                    openAddProviderInfoPricingModal({
                      providerId: provider.id,
                      providerInfos: [providerInfoRow],
                      onDone: refreshAndDeselect,
                    })
                  )
                }
              >
                Add
              </LinkButton>
            )}
          </TableCell>
          <TableCell>
            <ToggleField
              emphasized
              status={
                providerInfoRow.available && Object.keys(missingData).length > 0
                  ? 'warning'
                  : undefined
              }
              checked={providerInfoRow.available}
              onChange={value => handleAvailableToggleChange(value, [providerInfoRow.id])}
            />
          </TableCell>
        </>
      </TableRow>
    )
  }

  const renderFinishGroupRow = (providerInfoRowsOfFinishGroup: ProviderInfoRowContent[]) => {
    const finishGroup =
      finishGroups[materialConfigs[providerInfoRowsOfFinishGroup[0].materialConfigId].finishGroupId]

    const material = materials[finishGroup.materialId]
    const providerInfoIds = providerInfoRowsOfFinishGroup.map(providerInfo => providerInfo.id)

    const numAvailable = providerInfoRowsOfFinishGroup.filter(row => row.available).length
    const availableLabel = `${numAvailable}/${providerInfoIds.length}`

    const missingData = providerInfoRowsOfFinishGroup.reduce(
      (aggr, providerInfoRow) => ({
        ...aggr,
        ...(providerInfoRow.available ? getMissingProviderInfoData(providerInfoRow) : {}),
      }),
      {}
    )

    return (
      <ExpandableTableRow
        key={finishGroup.id}
        subRows={providerInfoRowsOfFinishGroup.map(row => renderMaterialConfigRow(row))}
        expanded={isExpanded(finishGroup.id)}
        toggle={() => toggleExpansion(finishGroup.id)}
      >
        {({expanded, expansionToggle}) => (
          <>
            <TableCell>
              <ActionBar space="90">
                <CheckboxField
                  checked={isAnySelected(providerInfoIds)}
                  partial={!areAllSelected(providerInfoIds)}
                  onChange={() => toggleAll(providerInfoIds)}
                />
                {expansionToggle}
              </ActionBar>
            </TableCell>
            <TableCell minor>{material.name}</TableCell>
            <TableCell strong>{finishGroup.name}</TableCell>
            <TableCell>
              <CounterTag disabled={expanded}>{providerInfoRowsOfFinishGroup.length}</CounterTag>
            </TableCell>
            <TableCell>
              {!expanded &&
                renderPrintingMethodAndInfills(providerInfoRowsOfFinishGroup, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && renderDimensions(providerInfoRowsOfFinishGroup, missingData)}
            </TableCell>
            <TableCell>
              {!expanded &&
                renderProductionTimeAndOptions(providerInfoRowsOfFinishGroup, missingData)}
            </TableCell>
            <TableCell>
              {!expanded &&
                renderDynamicProductionTimes(providerInfoRowsOfFinishGroup, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && renderPricing(providerInfoRowsOfFinishGroup, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && (
                <>
                  {numAvailable > 0 ? <strong>{availableLabel}</strong> : availableLabel}
                  <ToggleField
                    className="u-margin-left-s"
                    emphasized
                    status={Object.keys(missingData).length > 0 ? 'warning' : undefined}
                    checked={numAvailable > 0}
                    onChange={value => handleAvailableToggleChange(value, providerInfoIds)}
                  />
                </>
              )}
            </TableCell>
          </>
        )}
      </ExpandableTableRow>
    )
  }

  const renderMaterialRow = (providerInfoRowsOfMaterial: ProviderInfoRowContent[]) => {
    const material =
      materials[
        finishGroups[materialConfigs[providerInfoRowsOfMaterial[0].materialConfigId].finishGroupId]
          .materialId
      ]

    const finishGroupIds = uniq(
      providerInfoRowsOfMaterial.map(row => materialConfigs[row.materialConfigId].finishGroupId)
    )
    const providerInfoIds = providerInfoRowsOfMaterial.map(row => row.id)

    const numAvailable = providerInfoRowsOfMaterial.filter(
      providerInfo => providerInfo.available
    ).length
    const availableLabel = `${numAvailable}/${providerInfoIds.length}`

    const missingData = providerInfoRowsOfMaterial.reduce(
      (aggr, providerInfoRow) => ({
        ...aggr,
        ...(providerInfoRow.available ? getMissingProviderInfoData(providerInfoRow) : {}),
      }),
      {}
    )

    return (
      <ExpandableTableRow
        key={material.id}
        subRows={finishGroupIds.map(id => renderFinishGroupRow(tableRowsByFinishGroup[id]))}
        expanded={isExpanded(material.id)}
        toggle={() => toggleExpansion(material.id)}
      >
        {({expanded, expansionToggle}) => (
          <>
            <TableCell>
              <ActionBar space="90">
                <CheckboxField
                  checked={isAnySelected(providerInfoIds)}
                  partial={!areAllSelected(providerInfoIds)}
                  onChange={() => toggleAll(providerInfoIds)}
                />
                {expansionToggle}
                <MaterialImage
                  size="s"
                  square
                  image={getCloudinaryUrl(material.featuredImage, ['w_700', 'h_458', 'c_fill'])}
                />
              </ActionBar>
            </TableCell>
            <TableCell>
              <Link onClick={() => dispatch(openMaterialModal({materialId: material.id}))}>
                <strong>{material.name}</strong>
              </Link>
            </TableCell>
            <TableCell>
              <CounterTag disabled={expanded}>{finishGroupIds.length}</CounterTag>
            </TableCell>
            <TableCell>
              <CounterTag disabled={expanded}>{providerInfoRowsOfMaterial.length}</CounterTag>
            </TableCell>
            <TableCell>
              {!expanded && renderPrintingMethodAndInfills(providerInfoRowsOfMaterial, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && renderDimensions(providerInfoRowsOfMaterial, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && renderProductionTimeAndOptions(providerInfoRowsOfMaterial, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && renderDynamicProductionTimes(providerInfoRowsOfMaterial, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && renderPricing(providerInfoRowsOfMaterial, missingData)}
            </TableCell>
            <TableCell>
              {!expanded && (
                <>
                  {numAvailable > 0 ? <strong>{availableLabel}</strong> : availableLabel}
                  <ToggleField
                    className="u-margin-left-s"
                    emphasized
                    status={Object.keys(missingData).length ? 'warning' : undefined}
                    checked={numAvailable > 0}
                    onChange={value => handleAvailableToggleChange(value, providerInfoIds)}
                  />
                </>
              )}
            </TableCell>
          </>
        )}
      </ExpandableTableRow>
    )
  }

  const availableSelectedProviderInfoIds = selectedIds.filter(id => providerInfosById[id].available)

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={
              <ActionBar space="300">
                <Headline size="l" label="Materials & Configurations" />
                {!provider.online && (
                  <Stack space="60">
                    <Paragraph warning className="u-no-margin-bottom">
                      You are <strong>Offline</strong>.
                    </Paragraph>
                    <Paragraph minor size="xs" className="u-no-margin-bottom">
                      You can bring yourself back online in your profile.
                    </Paragraph>
                  </Stack>
                )}
              </ActionBar>
            }
            actions={
              <Button onClick={() => goToCreateProviderInfo(navigate)}>Add Materials</Button>
            }
          />
        }
      >
        <Container>
          <ActionHeaderLayout
            filterBar={
              <FilterBar
                filtersLabel="Filters:"
                filters={
                  <>
                    <StringMatchingFilterPartial
                      label="Technology"
                      size="90"
                      options={materialTechnologyOptions}
                      filterKey="technology"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('technology', newFilter)}
                    />
                    <StringMatchingFilterPartial
                      label="Printing method"
                      size="90"
                      options={printingMethodFilterOptions}
                      filterKey="printingMethod"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('printingMethod', newFilter)}
                      showSearch
                    />
                    <StringMatchingFilterPartial
                      label="Price"
                      size="90"
                      options={priceFilterOptions}
                      filterKey="price"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('price', newFilter)}
                      showSearch
                    />
                    <StringMatchingFilterPartial
                      label="Dynamic production time"
                      size="90"
                      options={dynamicProductionTimeOptions}
                      filterKey="dynamicProductionTime"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('dynamicProductionTime', newFilter)}
                      showSearch
                    />
                    <StringMatchingFilterPartial
                      label="Infill"
                      size="90"
                      options={infillFilterOptions}
                      filterKey="infill"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('infill', newFilter)}
                      showSearch
                    />
                    <StringMatchingFilterPartial
                      label="Production Time Option"
                      size="90"
                      options={productionTimeFilterOptions}
                      filterKey="productionTime"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('productionTime', newFilter)}
                      showSearch
                    />
                    <BooleanMatchingFilterPartial
                      label="Status"
                      size="90"
                      options={statusFilterOptions}
                      filterKey="status"
                      filterMap={filters}
                      tableRows={searchResults}
                      applyFilter={newFilter => setFilter('status', newFilter)}
                    />
                  </>
                }
                searchBar={
                  <SearchInputField
                    size="90"
                    name="provider-info-search"
                    placeholder="Search by material name"
                    value={searchValue}
                    onChange={value => {
                      setSearchValue(value)
                    }}
                  />
                }
              />
            }
            header={
              <ActionHeader
                left={
                  <LinkButton onClick={() => toggleAll()}>
                    {isAnySelected() ? 'Deselect all' : 'Select all'}
                  </LinkButton>
                }
                right={
                  <>
                    <PopoverHandler
                      distanceToAnchor={4}
                      popover={({close, anchorRect, ref, style}) => (
                        <ContextMenu
                          onClose={close}
                          style={{...style, minWidth: anchorRect?.width}}
                          ref={ref}
                        >
                          <ContextMenuLink
                            onClick={() => handleMenuItemClick(EditProviderInfoTab.MATERIAL_NAME)}
                          >
                            Name
                          </ContextMenuLink>
                          <ContextMenuLink
                            onClick={() => handleMenuItemClick(EditProviderInfoTab.PRINTING_METHOD)}
                          >
                            Printing method &amp; infills
                          </ContextMenuLink>
                          <ContextMenuLink
                            onClick={() => handleMenuItemClick(EditProviderInfoTab.DIMENSIONS)}
                          >
                            Dimensions
                          </ContextMenuLink>
                          <ContextMenuLink
                            onClick={() => handleMenuItemClick(EditProviderInfoTab.PRODUCTION_TIME)}
                          >
                            Production time
                          </ContextMenuLink>
                          <ContextMenuLink
                            onClick={() =>
                              handleMenuItemClick(EditProviderInfoTab.DYNAMIC_PRODUCTION_TIME)
                            }
                          >
                            Dynamic production time
                          </ContextMenuLink>
                          <ContextMenuLink
                            onClick={() => handleMenuItemClick(EditProviderInfoTab.PRICING)}
                          >
                            Pricing
                          </ContextMenuLink>
                        </ContextMenu>
                      )}
                    >
                      {({toggle, ref}) => (
                        <LinkButton
                          ref={ref}
                          size="200"
                          icon={<Icon source={editIcon} title="Edit" />}
                          disabled={selectedIds.length === 0}
                          onClick={toggle}
                        />
                      )}
                    </PopoverHandler>
                    <LinkButton
                      size="200"
                      icon={<Icon source={deleteIcon} title="Delete" />}
                      disabled={selectedIds.length === 0}
                      onClick={() => {
                        dispatch(
                          openConfirmDeleteModal({
                            itemsToDeleteCount: selectedIds.length,
                            onConfirm: () => {
                              providerInfoApi
                                .removeMultiple(selectedIds)
                                .catch(fatalError)
                                .then(refresh)
                            },
                          })
                        )
                      }}
                    />
                    <ToggleField
                      className="u-margin-right"
                      disabled={!isAnySelected()}
                      checked={!!availableSelectedProviderInfoIds.length}
                      onChange={value => handleAvailableToggleChange(value, selectedIds)}
                    />
                  </>
                }
              />
            }
          >
            <Table
              isLoading={isLoading}
              empty={
                tableRows.length === 0 ? (
                  'No Materials have been added yet.'
                ) : (
                  <Stack>
                    <div>No materials found</div>
                    <LinkButton
                      onClick={() => {
                        resetFilters()
                        setSearchValue('')
                      }}
                    >
                      Clear all filters
                    </LinkButton>
                  </Stack>
                )
              }
              header={
                <TableRow>
                  <TableHeaderCell width="100px" align="left">
                    <ActionBar space="90">
                      <TableRowToggleButton
                        className="u-margin-left-l"
                        expanded={isAnyExpanded()}
                        onClick={() => {
                          setAllExpanded(!isAnyExpanded())
                        }}
                      />
                    </ActionBar>
                  </TableHeaderCell>
                  <TableHeaderCell width={4}>Material</TableHeaderCell>
                  <TableHeaderCell width={3}>Finishes</TableHeaderCell>
                  <TableHeaderCell width={3}>Colors</TableHeaderCell>
                  <TableHeaderCell width={3}>Method &amp; Infill</TableHeaderCell>
                  <TableHeaderCell width={4}>Dimensions</TableHeaderCell>
                  <TableHeaderCell width={3}>Production time</TableHeaderCell>
                  <TableHeaderCell width={3} align="right">
                    Dynamic Production time
                  </TableHeaderCell>
                  <TableHeaderCell width={3} align="right">
                    Pricing
                  </TableHeaderCell>
                  <TableHeaderCell width="100px" align="right">
                    Live
                  </TableHeaderCell>
                </TableRow>
              }
            >
              {materialTableRows.map(renderMaterialRow)}
            </Table>
          </ActionHeaderLayout>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default ProviderInfoPage
